// Chess — pure rules engine + computer player (no I/O; imports only the
// shared types), so the same file runs in the `games` edge function (Deno),
// in the browser (legal-move hints, SAN) and under node tests.
//
// Rules: full FIDE movement — castling (not out of, through or into check),
// en passant, promotion (Q/R/B/N, the player chooses), check, checkmate,
// stalemate. Draws are automatic on threefold repetition, the 50-move rule
// (100 plies with no pawn move or capture) and insufficient material; or by
// agreement (offer / accept). Resign any time. Optional clocks (5 or 10 min
// per side, no increment) run on the framework's game clock (wakeAt/onWake):
// a flag loses unless the opponent can't possibly mate (then it's a draw).
//
// Seats: 0 = White, 1 = Black. The public state stores the position as FEN.
//
// Engine: 0..63 squares (a1 = 0, h1 = 7, a8 = 56), piece codes
// type | colour<<3 (P1 N2 B3 R4 Q5 K6, black +8), copy-make positions,
// legal = pseudo-legal minus moves that leave the own king attacked.
// Verified by perft (tests/games/chess.test.ts).

import { RuleError, type Ctx, type GameDef, type SeatResult } from './types.ts';
export { RuleError };

export const SEAT_NAMES = ['White', 'Black'];

// ---------------------------------------------------------------------------
// Board representation
// ---------------------------------------------------------------------------

export const PAWN = 1, KNIGHT = 2, BISHOP = 3, ROOK = 4, QUEEN = 5, KING = 6;
const BLACK_BIT = 8;
const typeOf = (p: number) => p & 7;
const colorOf = (p: number) => p >> 3; // 0 white, 1 black
const fileOf = (s: number) => s & 7;
const rankOf = (s: number) => s >> 3;

export interface Pos {
  b: Int8Array;          // 64 squares
  side: 0 | 1;           // to move
  castle: number;        // K=1 Q=2 k=4 q=8
  ep: number;            // en-passant target square or -1
  half: number;          // plies since the last pawn move or capture
  full: number;          // move number
  kings: [number, number];
}

// Move encoding: from | to<<6 | promoType<<12 | flags<<15
const F_CAP = 1, F_EP = 2, F_CASTLE = 4, F_DOUBLE = 8;
const mFrom = (m: number) => m & 63;
const mTo = (m: number) => (m >> 6) & 63;
const mPromo = (m: number) => (m >> 12) & 7;
const mFlags = (m: number) => m >> 15;
const enc = (from: number, to: number, promo = 0, flags = 0) => from | (to << 6) | (promo << 12) | (flags << 15);

// Precomputed attack tables.
const KNIGHT_T: number[][] = [];
const KING_T: number[][] = [];
const DIRS: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
const RAYS: number[][][] = []; // RAYS[sq][dir] = squares in order (dirs 0-3 orthogonal, 4-7 diagonal)
for (let s = 0; s < 64; s++) {
  const f = fileOf(s), r = rankOf(s);
  const on = (ff: number, rr: number) => ff >= 0 && ff < 8 && rr >= 0 && rr < 8;
  KNIGHT_T[s] = [[1, 2], [2, 1], [2, -1], [1, -2], [-1, -2], [-2, -1], [-2, 1], [-1, 2]]
    .filter(([df, dr]) => on(f + df, r + dr)).map(([df, dr]) => (r + dr) * 8 + f + df);
  KING_T[s] = DIRS.filter(([df, dr]) => on(f + df, r + dr)).map(([df, dr]) => (r + dr) * 8 + f + df);
  RAYS[s] = DIRS.map(([df, dr]) => {
    const out: number[] = [];
    for (let k = 1; on(f + df * k, r + dr * k); k++) out.push((r + dr * k) * 8 + f + df * k);
    return out;
  });
}
// Castling rights kept after a move touches a square.
const CASTLE_KEEP = new Array(64).fill(15);
CASTLE_KEEP[0] = 15 & ~2; CASTLE_KEEP[7] = 15 & ~1; CASTLE_KEEP[4] = 15 & ~3;
CASTLE_KEEP[56] = 15 & ~8; CASTLE_KEEP[63] = 15 & ~4; CASTLE_KEEP[60] = 15 & ~12;

export const sqName = (s: number) => 'abcdefgh'[fileOf(s)] + (rankOf(s) + 1);
export function sqIndex(name: string): number {
  if (typeof name !== 'string' || !/^[a-h][1-8]$/.test(name)) return -1;
  return (name.charCodeAt(1) - 49) * 8 + (name.charCodeAt(0) - 97);
}

const PIECE_CH = ' pnbrqk';
export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export function parseFen(fen: string): Pos {
  const [placement, side, castle, ep, half, full] = fen.trim().split(/\s+/);
  const b = new Int8Array(64);
  const kings: [number, number] = [-1, -1];
  const rows = placement.split('/');
  if (rows.length !== 8) throw new Error('Bad FEN');
  for (let i = 0; i < 8; i++) {
    let f = 0;
    const r = 7 - i;
    for (const ch of rows[i]) {
      if (/\d/.test(ch)) { f += Number(ch); continue; }
      const t = PIECE_CH.indexOf(ch.toLowerCase());
      if (t <= 0 || f > 7) throw new Error('Bad FEN');
      const c = ch === ch.toLowerCase() ? 1 : 0;
      b[r * 8 + f] = t | (c << 3);
      if (t === KING) kings[c] = r * 8 + f;
      f++;
    }
  }
  let cr = 0;
  if (castle && castle !== '-') for (const ch of castle) cr |= ch === 'K' ? 1 : ch === 'Q' ? 2 : ch === 'k' ? 4 : ch === 'q' ? 8 : 0;
  return { b, side: side === 'b' ? 1 : 0, castle: cr, ep: ep && ep !== '-' ? sqIndex(ep) : -1, half: Number(half) || 0, full: Number(full) || 1, kings };
}

function placementOf(b: Int8Array): string {
  const rows: string[] = [];
  for (let r = 7; r >= 0; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const p = b[r * 8 + f];
      if (!p) { empty++; continue; }
      if (empty) { row += empty; empty = 0; }
      const ch = PIECE_CH[typeOf(p)];
      row += colorOf(p) ? ch : ch.toUpperCase();
    }
    if (empty) row += empty;
    rows.push(row);
  }
  return rows.join('/');
}
const castleStr = (c: number) => (c & 1 ? 'K' : '') + (c & 2 ? 'Q' : '') + (c & 4 ? 'k' : '') + (c & 8 ? 'q' : '') || '-';

export function toFen(p: Pos): string {
  return `${placementOf(p.b)} ${p.side ? 'b' : 'w'} ${castleStr(p.castle)} ${p.ep >= 0 ? sqName(p.ep) : '-'} ${p.half} ${p.full}`;
}

/** Is `sq` attacked by colour `by`? */
export function attacked(p: Pos, sq: number, by: number): boolean {
  const b = p.b;
  const f = fileOf(sq);
  if (by === 0) {
    if (f > 0 && sq >= 9 && b[sq - 9] === PAWN) return true;
    if (f < 7 && sq >= 7 && b[sq - 7] === PAWN) return true;
  } else {
    if (f > 0 && sq + 7 < 64 && b[sq + 7] === (PAWN | BLACK_BIT)) return true;
    if (f < 7 && sq + 9 < 64 && b[sq + 9] === (PAWN | BLACK_BIT)) return true;
  }
  const bb = by << 3;
  for (const t of KNIGHT_T[sq]) if (b[t] === (KNIGHT | bb)) return true;
  for (const t of KING_T[sq]) if (b[t] === (KING | bb)) return true;
  const rays = RAYS[sq];
  for (let d = 0; d < 8; d++) {
    const ray = rays[d];
    for (let k = 0; k < ray.length; k++) {
      const q = b[ray[k]];
      if (!q) continue;
      if (colorOf(q) === by) {
        const t = typeOf(q);
        if (t === QUEEN || (d < 4 ? t === ROOK : t === BISHOP)) return true;
      }
      break;
    }
  }
  return false;
}

export const inCheck = (p: Pos) => attacked(p, p.kings[p.side], p.side ^ 1);

/** Pseudo-legal moves (captures/promotions only when `tactical`). */
function genMoves(p: Pos, tactical = false): number[] {
  const out: number[] = [];
  const b = p.b, side = p.side, me = side;
  const dir = side ? -8 : 8;
  const startRank = side ? 6 : 1, promoRank = side ? 0 : 7;
  for (let s = 0; s < 64; s++) {
    const pc = b[s];
    if (!pc || colorOf(pc) !== me) continue;
    const t = typeOf(pc);
    if (t === PAWN) {
      const one = s + dir;
      if (!b[one]) {
        if (rankOf(one) === promoRank) {
          out.push(enc(s, one, QUEEN));
          if (!tactical) { out.push(enc(s, one, ROOK)); out.push(enc(s, one, BISHOP)); out.push(enc(s, one, KNIGHT)); }
        } else if (!tactical) {
          out.push(enc(s, one));
          if (rankOf(s) === startRank && !b[one + dir]) out.push(enc(s, one + dir, 0, F_DOUBLE));
        }
      }
      for (const df of [-1, 1]) {
        const f = fileOf(s) + df;
        if (f < 0 || f > 7) continue;
        const to = one + df;
        const q = b[to];
        if (q && colorOf(q) !== me) {
          if (rankOf(to) === promoRank) {
            out.push(enc(s, to, QUEEN, F_CAP));
            if (!tactical) { out.push(enc(s, to, ROOK, F_CAP)); out.push(enc(s, to, BISHOP, F_CAP)); out.push(enc(s, to, KNIGHT, F_CAP)); }
          } else out.push(enc(s, to, 0, F_CAP));
        } else if (to === p.ep) out.push(enc(s, to, 0, F_EP | F_CAP));
      }
      continue;
    }
    if (t === KNIGHT || t === KING) {
      for (const to of (t === KNIGHT ? KNIGHT_T : KING_T)[s]) {
        const q = b[to];
        if (!q) { if (!tactical) out.push(enc(s, to)); } else if (colorOf(q) !== me) out.push(enc(s, to, 0, F_CAP));
      }
      if (t === KING && !tactical) castles(p, s, out);
      continue;
    }
    const d0 = t === BISHOP ? 4 : 0, d1 = t === ROOK ? 4 : 8;
    for (let d = d0; d < d1; d++) {
      for (const to of RAYS[s][d]) {
        const q = b[to];
        if (!q) { if (!tactical) out.push(enc(s, to)); continue; }
        if (colorOf(q) !== me) out.push(enc(s, to, 0, F_CAP));
        break;
      }
    }
  }
  return out;
}

function castles(p: Pos, s: number, out: number[]) {
  const b = p.b, side = p.side, opp = side ^ 1;
  const home = side ? 60 : 4;
  if (s !== home) return;
  const rook = ROOK | (side << 3);
  const kBit = side ? 4 : 1, qBit = side ? 8 : 2;
  if (p.castle & kBit && !b[home + 1] && !b[home + 2] && b[home + 3] === rook &&
      !attacked(p, home, opp) && !attacked(p, home + 1, opp) && !attacked(p, home + 2, opp)) {
    out.push(enc(home, home + 2, 0, F_CASTLE));
  }
  if (p.castle & qBit && !b[home - 1] && !b[home - 2] && !b[home - 3] && b[home - 4] === rook &&
      !attacked(p, home, opp) && !attacked(p, home - 1, opp) && !attacked(p, home - 2, opp)) {
    out.push(enc(home, home - 2, 0, F_CASTLE));
  }
}

/** The position after move `m` (copy-make). */
export function makeMove(p: Pos, m: number): Pos {
  const b = p.b.slice() as Int8Array;
  const from = mFrom(m), to = mTo(m), promo = mPromo(m), fl = mFlags(m);
  const pc = b[from];
  const side = p.side;
  const captured = b[to];
  b[to] = promo ? promo | (side << 3) : pc;
  b[from] = 0;
  if (fl & F_EP) b[to + (side ? 8 : -8)] = 0;
  if (fl & F_CASTLE) {
    if (to === from + 2) { b[from + 1] = b[from + 3]; b[from + 3] = 0; } else { b[from - 1] = b[from - 4]; b[from - 4] = 0; }
  }
  const kings: [number, number] = [p.kings[0], p.kings[1]];
  if (typeOf(pc) === KING) kings[side] = to;
  return {
    b,
    side: (side ^ 1) as 0 | 1,
    castle: p.castle & CASTLE_KEEP[from] & CASTLE_KEEP[to],
    ep: fl & F_DOUBLE ? (from + to) >> 1 : -1,
    half: typeOf(pc) === PAWN || captured || fl & F_EP ? 0 : p.half + 1,
    full: side ? p.full + 1 : p.full,
    kings,
  };
}

/** Legal moves as encoded numbers. */
export function legalMoves(p: Pos): number[] {
  const out: number[] = [];
  for (const m of genMoves(p)) {
    const n = makeMove(p, m);
    if (!attacked(n, n.kings[p.side], n.side)) out.push(m);
  }
  return out;
}

export function perft(p: Pos, depth: number): number {
  if (depth === 0) return 1;
  let n = 0;
  for (const m of genMoves(p)) {
    const q = makeMove(p, m);
    if (attacked(q, q.kings[p.side], q.side)) continue;
    n += depth === 1 ? 1 : perft(q, depth - 1);
  }
  return n;
}

export interface MoveInfo { from: number; to: number; promo?: 'q' | 'r' | 'b' | 'n' }
const PROMO_CH = ['', '', 'n', 'b', 'r', 'q'];
const PROMO_T: Record<string, number> = { n: KNIGHT, b: BISHOP, r: ROOK, q: QUEEN };
const moveInfo = (m: number): MoveInfo => {
  const pr = mPromo(m);
  return pr ? { from: mFrom(m), to: mTo(m), promo: PROMO_CH[pr] as MoveInfo['promo'] } : { from: mFrom(m), to: mTo(m) };
};

/** Legal moves of a FEN position as { from, to, promo? } (for clients). */
export function legalMoveList(fen: string): MoveInfo[] {
  return legalMoves(parseFen(fen)).map(moveInfo);
}

/** Standard algebraic notation for legal move `m` in `p`. */
export function san(p: Pos, m: number, legal = legalMoves(p)): string {
  const from = mFrom(m), to = mTo(m), fl = mFlags(m), pr = mPromo(m);
  const t = typeOf(p.b[from]);
  let s: string;
  if (fl & F_CASTLE) s = to > from ? 'O-O' : 'O-O-O';
  else if (t === PAWN) {
    s = (fl & F_CAP ? 'abcdefgh'[fileOf(from)] + 'x' : '') + sqName(to) + (pr ? '=' + PROMO_CH[pr].toUpperCase() : '');
  } else {
    const others = legal.filter((o) => mTo(o) === to && mFrom(o) !== from && typeOf(p.b[mFrom(o)]) === t);
    let dis = '';
    if (others.length) {
      if (!others.some((o) => fileOf(mFrom(o)) === fileOf(from))) dis = 'abcdefgh'[fileOf(from)];
      else if (!others.some((o) => rankOf(mFrom(o)) === rankOf(from))) dis = String(rankOf(from) + 1);
      else dis = sqName(from);
    }
    s = ' PNBRQK'[t] + dis + (fl & F_CAP ? 'x' : '') + sqName(to);
  }
  const n = makeMove(p, m);
  if (inCheck(n)) s += legalMoves(n).length ? '+' : '#';
  return s;
}

/** Neither side can ever mate: K v K, K+minor v K, or only bishops all on one square colour. */
export function insufficientMaterial(p: Pos): boolean {
  let minors = 0, knights = 0;
  const bishopColours = new Set<number>();
  for (let s = 0; s < 64; s++) {
    const t = typeOf(p.b[s]);
    if (!t || t === KING) continue;
    if (t === PAWN || t === ROOK || t === QUEEN) return false;
    minors++;
    if (t === KNIGHT) knights++;
    else bishopColours.add((fileOf(s) + rankOf(s)) & 1);
  }
  if (minors <= 1) return true;
  return knights === 0 && bishopColours.size === 1;
}

/** Could colour `c` deliver mate at all (for a flag: the flagger only loses if the opponent can mate)? */
function canEverMate(p: Pos, c: number): boolean {
  let minors = 0;
  for (let s = 0; s < 64; s++) {
    const q = p.b[s];
    if (!q || colorOf(q) !== c) continue;
    const t = typeOf(q);
    if (t === PAWN || t === ROOK || t === QUEEN) return true;
    if (t === KNIGHT || t === BISHOP) minors++;
  }
  return minors >= 2 || (minors === 1 && !insufficientMaterial(p));
}

/** Repetition key: placement, side, castling and an en-passant square only when a capture there is possible. */
export function posKey(p: Pos): string {
  let ep = '-';
  if (p.ep >= 0) {
    const pawn = PAWN | (p.side << 3);
    const from = p.ep + (p.side ? 8 : -8);
    if ((fileOf(p.ep) > 0 && p.b[from - 1] === pawn) || (fileOf(p.ep) < 7 && p.b[from + 1] === pawn)) ep = sqName(p.ep);
  }
  return `${placementOf(p.b)} ${p.side} ${p.castle} ${ep}`;
}

// ---------------------------------------------------------------------------
// Game state (public)
// ---------------------------------------------------------------------------

export type ChessLevel = 'easy' | 'medium' | 'hard';
export type ChessClock = 'none' | '10' | '5';
export interface ChessOptions { level: ChessLevel; clock: ChessClock }
export const DEFAULT_OPTIONS: ChessOptions = { level: 'medium', clock: 'none' };

export type EndReason = 'checkmate' | 'stalemate' | 'resign' | 'timeout' | 'repetition' | 'fifty' | 'material' | 'agreement' | 'abandoned' | 'timeout-material';

export interface ChessState {
  fen: string;
  /** SAN of every move so far. */
  moves: string[];
  /** The last move (squares 0..63), for highlighting and the slide animation. */
  last: { from: number; to: number } | null;
  /** Repetition keys since the last irreversible move (pawn move / capture / castling-right change). */
  reps: string[];
  check: boolean;
  result: null | { winner: 0 | 1 | null; reason: EndReason };
  /** Seat with a standing draw offer. */
  drawOffer: 0 | 1 | null;
  /** Draw offers made so far per seat (capped, so nobody can spam). */
  offers: [number, number];
  /** Clocks (ms left per seat; `since` = when the side to move started thinking). */
  clock: null | { ms: [number, number]; since: number; base: number };
  level: ChessLevel;
}

export type ChessMove =
  | { type: 'move'; from: string; to: string; promo?: 'q' | 'r' | 'b' | 'n' }
  | { type: 'resign' }
  | { type: 'offer_draw' }
  | { type: 'accept_draw' }
  | { type: 'decline_draw' };

export const MAX_DRAW_OFFERS = 3;
export const IDLE_FORFEIT_MS = 10 * 60_000;

export const turnOf = (g: ChessState): 0 | 1 => (g.fen.split(' ')[1] === 'b' ? 1 : 0);

export function newChess(opts: Partial<ChessOptions> | null | undefined, now: number): ChessState {
  const o = { ...DEFAULT_OPTIONS, ...(opts ?? {}) };
  const p = parseFen(START_FEN);
  const base = o.clock === '10' ? 600_000 : o.clock === '5' ? 300_000 : 0;
  return {
    fen: START_FEN, moves: [], last: null, reps: [posKey(p)], check: false, result: null,
    drawOffer: null, offers: [0, 0], level: o.level,
    clock: base ? { ms: [base, base], since: now, base } : null,
  };
}

/** Clock ms left for `seat` at `now` (counts down only for the side to move). */
export function clockLeft(g: ChessState, seat: number, now: number): number | null {
  if (!g.clock) return null;
  const run = !g.result && turnOf(g) === seat ? now - g.clock.since : 0;
  return Math.max(0, g.clock.ms[seat] - run);
}

/** The side to move ran out of time. */
function flag(g: ChessState, now: number): ChessState {
  const side = turnOf(g);
  const p = parseFen(g.fen);
  const ms: [number, number] = [g.clock!.ms[0], g.clock!.ms[1]];
  ms[side] = 0;
  const opp = (side ^ 1) as 0 | 1;
  const result = canEverMate(p, opp) ? { winner: opp, reason: 'timeout' as EndReason } : { winner: null, reason: 'timeout-material' as EndReason };
  return { ...g, clock: { ...g.clock!, ms, since: now }, result, drawOffer: null };
}

/** Apply a board move for `seat` (validated). */
export function applyChessMove(g: ChessState, seat: number, mv: { from: unknown; to: unknown; promo?: unknown }, now: number): { g: ChessState; san: string } {
  if (g.result) throw new RuleError('The game is over');
  const side = turnOf(g);
  if (seat !== side) throw new RuleError('Not your turn');
  const from = sqIndex(String(mv.from)), to = sqIndex(String(mv.to));
  if (from < 0 || to < 0) throw new RuleError('Bad square');
  const p = parseFen(g.fen);
  const legal = legalMoves(p);
  const cands = legal.filter((m) => mFrom(m) === from && mTo(m) === to);
  if (!cands.length) throw new RuleError(p.b[from] && colorOf(p.b[from]) === side ? 'That move is not legal' : 'Pick one of your pieces');
  let m = cands[0];
  if (cands.length > 1 || mPromo(m)) {
    const pr = PROMO_T[String(mv.promo ?? '')];
    if (!pr) throw new RuleError('Choose a piece to promote to');
    m = cands.find((c) => mPromo(c) === pr)!;
  }
  const s = san(p, m, legal);
  const n = makeMove(p, m);
  const key = posKey(n);
  const irreversible = n.half === 0 || n.castle !== p.castle;
  const reps = irreversible ? [key] : [...g.reps, key];
  let clock = g.clock;
  if (clock) {
    const ms: [number, number] = [clock.ms[0], clock.ms[1]];
    ms[side] = Math.max(1, ms[side] - (now - clock.since));
    clock = { ...clock, ms, since: now };
  }
  const nextLegal = legalMoves(n).length;
  const check = inCheck(n);
  let result: ChessState['result'] = null;
  if (!nextLegal) result = check ? { winner: side, reason: 'checkmate' } : { winner: null, reason: 'stalemate' };
  else if (insufficientMaterial(n)) result = { winner: null, reason: 'material' };
  else if (reps.filter((k) => k === key).length >= 3) result = { winner: null, reason: 'repetition' };
  else if (n.half >= 100) result = { winner: null, reason: 'fifty' };
  return {
    san: s,
    g: {
      ...g, fen: toFen(n), moves: [...g.moves, s], last: { from, to }, reps, check, result, clock,
      // Moving declines the opponent's offer; your own offer stands for their reply.
      drawOffer: g.drawOffer === side ? side : null,
    },
  };
}

export function resultText(g: ChessState): string {
  const r = g.result;
  if (!r) return '';
  const who = r.winner === null ? '' : `${SEAT_NAMES[r.winner]} wins`;
  switch (r.reason) {
    case 'checkmate': return `Checkmate — ${who}`;
    case 'resign': return `${SEAT_NAMES[r.winner === 0 ? 1 : 0]} resigned — ${who}`;
    case 'timeout': return `${SEAT_NAMES[r.winner === 0 ? 1 : 0]} ran out of time — ${who}`;
    case 'timeout-material': return 'Out of time, but no mating material — draw';
    case 'abandoned': return `${SEAT_NAMES[r.winner === 0 ? 1 : 0]} stopped moving — ${who}`;
    case 'stalemate': return 'Stalemate — draw';
    case 'repetition': return 'Threefold repetition — draw';
    case 'fifty': return '50-move rule — draw';
    case 'material': return 'Insufficient material — draw';
    case 'agreement': return 'Draw agreed';
  }
}

// ---------------------------------------------------------------------------
// Computer player: iterative-deepening alpha-beta, material + piece-square
// tables, a capture-only quiescence search, MVV-LVA ordering and a node
// budget (deterministic; keeps every move well under ~0.3 s of CPU).
// ---------------------------------------------------------------------------

const VAL = [0, 100, 320, 330, 500, 900, 0];
// Piece-square tables, written rank 8 → rank 1 from White's point of view.
const PST_SRC: Record<number, number[]> = {
  [PAWN]: [0, 0, 0, 0, 0, 0, 0, 0, 50, 50, 50, 50, 50, 50, 50, 50, 10, 10, 20, 30, 30, 20, 10, 10, 5, 5, 10, 25, 25, 10, 5, 5, 0, 0, 0, 20, 20, 0, 0, 0, 5, -5, -10, 0, 0, -10, -5, 5, 5, 10, 10, -20, -20, 10, 10, 5, 0, 0, 0, 0, 0, 0, 0, 0],
  [KNIGHT]: [-50, -40, -30, -30, -30, -30, -40, -50, -40, -20, 0, 0, 0, 0, -20, -40, -30, 0, 10, 15, 15, 10, 0, -30, -30, 5, 15, 20, 20, 15, 5, -30, -30, 0, 15, 20, 20, 15, 0, -30, -30, 5, 10, 15, 15, 10, 5, -30, -40, -20, 0, 5, 5, 0, -20, -40, -50, -40, -30, -30, -30, -30, -40, -50],
  [BISHOP]: [-20, -10, -10, -10, -10, -10, -10, -20, -10, 0, 0, 0, 0, 0, 0, -10, -10, 0, 5, 10, 10, 5, 0, -10, -10, 5, 5, 10, 10, 5, 5, -10, -10, 0, 10, 10, 10, 10, 0, -10, -10, 10, 10, 10, 10, 10, 10, -10, -10, 5, 0, 0, 0, 0, 5, -10, -20, -10, -10, -10, -10, -10, -10, -20],
  [ROOK]: [0, 0, 0, 0, 0, 0, 0, 0, 5, 10, 10, 10, 10, 10, 10, 5, -5, 0, 0, 0, 0, 0, 0, -5, -5, 0, 0, 0, 0, 0, 0, -5, -5, 0, 0, 0, 0, 0, 0, -5, -5, 0, 0, 0, 0, 0, 0, -5, -5, 0, 0, 0, 0, 0, 0, -5, 0, 0, 0, 5, 5, 0, 0, 0],
  [QUEEN]: [-20, -10, -10, -5, -5, -10, -10, -20, -10, 0, 0, 0, 0, 0, 0, -10, -10, 0, 5, 5, 5, 5, 0, -10, -5, 0, 5, 5, 5, 5, 0, -5, 0, 0, 5, 5, 5, 5, 0, -5, -10, 5, 5, 5, 5, 5, 0, -10, -10, 0, 5, 0, 0, 0, 0, -10, -20, -10, -10, -5, -5, -10, -10, -20],
  [KING]: [-30, -40, -40, -50, -50, -40, -40, -30, -30, -40, -40, -50, -50, -40, -40, -30, -30, -40, -40, -50, -50, -40, -40, -30, -30, -40, -40, -50, -50, -40, -40, -30, -20, -30, -30, -40, -40, -30, -30, -20, -10, -20, -20, -20, -20, -20, -20, -10, 20, 20, 0, 0, 0, 0, 20, 20, 20, 30, 10, 0, 0, 10, 30, 20],
};
const KING_END_SRC = [-50, -40, -30, -20, -20, -30, -40, -50, -30, -20, -10, 0, 0, -10, -20, -30, -30, -10, 20, 30, 30, 20, -10, -30, -30, -10, 30, 40, 40, 30, -10, -30, -30, -10, 30, 40, 40, 30, -10, -30, -30, -10, 20, 30, 30, 20, -10, -30, -30, -30, 0, 0, 0, 0, -30, -30, -50, -30, -30, -30, -30, -30, -30, -50];
// PST[colour][type][sq] in engine square order.
const PST: number[][][] = [0, 1].map((c) => [0, 1, 2, 3, 4, 5, 6, 7].map((t) => {
  const src = t === 7 ? KING_END_SRC : PST_SRC[t];
  const out = new Array(64).fill(0);
  if (!src) return out;
  for (let s = 0; s < 64; s++) {
    const r = rankOf(s), f = fileOf(s);
    out[s] = c === 0 ? src[(7 - r) * 8 + f] : src[r * 8 + f];
  }
  return out;
}));

/** Static evaluation from the side to move's point of view (centipawns). */
export function evaluate(p: Pos): number {
  let score = 0, heavy = 0;
  for (let s = 0; s < 64; s++) {
    const q = p.b[s];
    const t = typeOf(q);
    if (t > PAWN && t < KING) heavy += VAL[t];
  }
  const endgame = heavy <= 1300;
  for (let s = 0; s < 64; s++) {
    const q = p.b[s];
    if (!q) continue;
    const t = typeOf(q), c = colorOf(q);
    const v = VAL[t] + PST[c][t === KING && endgame ? 7 : t][s];
    score += c === 0 ? v : -v;
  }
  return p.side === 0 ? score : -score;
}

const MATE = 100_000;

interface Search { nodes: number; limit: number; aborted: boolean; qDepth: number }

function orderMoves(p: Pos, moves: number[]): number[] {
  const key = (m: number) => {
    let k = 0;
    if (mFlags(m) & F_CAP) k += 10 * VAL[typeOf(p.b[mTo(m)]) || PAWN] - VAL[typeOf(p.b[mFrom(m)])] + 10_000;
    if (mPromo(m)) k += VAL[mPromo(m)] + 5_000;
    return k;
  };
  return moves.map((m) => [key(m), m]).sort((a, b) => b[0] - a[0]).map((x) => x[1]);
}

function quiesce(p: Pos, alpha: number, beta: number, depth: number, S: Search): number {
  S.nodes++;
  const stand = evaluate(p);
  if (stand >= beta) return beta;
  if (stand > alpha) alpha = stand;
  if (depth <= 0) return alpha;
  for (const m of orderMoves(p, genMoves(p, true))) {
    const n = makeMove(p, m);
    if (attacked(n, n.kings[p.side], n.side)) continue;
    const sc = -quiesce(n, -beta, -alpha, depth - 1, S);
    if (sc >= beta) return beta;
    if (sc > alpha) alpha = sc;
  }
  return alpha;
}

function negamax(p: Pos, depth: number, alpha: number, beta: number, ply: number, S: Search): number {
  if (++S.nodes > S.limit) { S.aborted = true; return 0; }
  if (p.half >= 100) return 0;
  if (depth <= 0) return S.qDepth > 0 ? quiesce(p, alpha, beta, S.qDepth, S) : evaluate(p);
  let legal = 0;
  for (const m of orderMoves(p, genMoves(p))) {
    const n = makeMove(p, m);
    if (attacked(n, n.kings[p.side], n.side)) continue;
    legal++;
    const sc = -negamax(n, depth - 1, -beta, -alpha, ply + 1, S);
    if (S.aborted) return 0;
    if (sc >= beta) return beta;
    if (sc > alpha) alpha = sc;
  }
  if (!legal) return inCheck(p) ? -MATE + ply : 0;
  return alpha;
}

const LEVELS: Record<ChessLevel, { depth: number; q: number; limit: number; noise: number; random: number }> = {
  easy: { depth: 1, q: 0, limit: 5_000, noise: 90, random: 0.15 },
  medium: { depth: 2, q: 4, limit: 25_000, noise: 20, random: 0 },
  hard: { depth: 4, q: 6, limit: 200_000, noise: 4, random: 0 },
};

/** Pick a move for the side to move. `reps` = repetition history (repeats score as a draw). */
export function chooseMove(p: Pos, level: ChessLevel, rng: () => number, reps: string[] = []): number {
  const L = LEVELS[level] ?? LEVELS.medium;
  const legal = legalMoves(p);
  if (!legal.length) throw new Error('No legal moves');
  if (legal.length === 1) return legal[0];
  if (L.random && rng() < L.random) return legal[Math.floor(rng() * legal.length)];
  const S: Search = { nodes: 0, limit: L.limit, aborted: false, qDepth: L.q };
  const noise = new Map(legal.map((m) => [m, (rng() - 0.5) * 2 * L.noise]));
  const repeats = new Set(legal.filter((m) => reps.includes(posKey(makeMove(p, m)))));
  let order = orderMoves(p, legal);
  let best = order[0];
  for (let d = 1; d <= L.depth; d++) {
    const scored: [number, number][] = [];
    let alpha = -MATE * 2;
    for (const m of order) {
      const n = makeMove(p, m);
      let sc = -negamax(n, d - 1, -MATE * 2, -alpha + L.noise * 2 + 1, 1, S);
      if (S.aborted) break;
      if (repeats.has(m)) sc = 0;          // heading for a repetition draw
      if (Math.abs(sc) < MATE / 2) sc += noise.get(m)!;
      scored.push([sc, m]);
      if (sc > alpha) alpha = sc;
    }
    if (S.aborted) break;
    scored.sort((a, b) => b[0] - a[0]);
    best = scored[0][1];
    order = scored.map((x) => x[1]);
    if (scored[0][0] > MATE / 2) break; // found a mate
  }
  return best;
}

/** Bot move for the framework. */
export function botChessMove(g: ChessState, seat: number, rng: () => number): ChessMove {
  const p = parseFen(g.fen);
  // A standing draw offer: take it when clearly worse.
  if (g.drawOffer === (seat ^ 1)) {
    const sc = quiesce(p, -MATE, MATE, 4, { nodes: 0, limit: 1e9, aborted: false, qDepth: 4 });
    if (sc < -150) return { type: 'accept_draw' };
  }
  const m = chooseMove(p, g.level, rng, g.reps);
  const info = moveInfo(m);
  return { type: 'move', from: sqName(info.from), to: sqName(info.to), ...(info.promo ? { promo: info.promo } : {}) };
}

// ---------------------------------------------------------------------------
// Table framework hookup (registered in registry.ts)
// ---------------------------------------------------------------------------

function configureChess(cur: any, patch: unknown): ChessOptions {
  const base: ChessOptions = { ...DEFAULT_OPTIONS, ...(cur ?? {}) };
  if (!patch || typeof patch !== 'object') throw new RuleError('Bad options');
  const p = patch as Record<string, unknown>;
  if (p.level !== undefined) {
    if (p.level !== 'easy' && p.level !== 'medium' && p.level !== 'hard') throw new RuleError('Level must be easy, medium or hard');
    base.level = p.level;
  }
  if (p.clock !== undefined) {
    if (p.clock !== 'none' && p.clock !== '10' && p.clock !== '5') throw new RuleError('Clock must be none, 10 or 5 minutes');
    base.clock = p.clock;
  }
  return base;
}

const nameAt = (ctx: Ctx, seat: number) => ctx.seats[seat]?.name || SEAT_NAMES[seat];

export const chessGame: GameDef<ChessState, ChessMove> = {
  kind: 'chess',
  label: 'Chess',
  minPlayers: 2,
  maxPlayers: 2,
  seatNames: SEAT_NAMES,
  // Thinking is allowed; a player who goes quiet for 10 minutes forfeits (onIdle).
  turnTimeoutMs: IDLE_FORFEIT_MS,
  configure: configureChess,
  start: (_prev, ctx) => ({ g: newChess(ctx.options, ctx.now), note: 'White to move' }),
  turnSeat: (g) => (g.result ? null : turnOf(g)),
  // Either player may resign or offer / answer a draw at any time; board moves check the turn.
  canAct: (g, actor) => !g.result && (actor === 0 || actor === 1),
  move: (g, _privs, actor, m, ctx) => {
    const type = (m as any)?.type;
    const me = nameAt(ctx, actor);
    if (type === 'move') {
      if (g.clock && turnOf(g) === actor && (clockLeft(g, actor, ctx.now) ?? 1) <= 0) {
        return { g: flag(g, ctx.now), note: `${me} ran out of time` };
      }
      const r = applyChessMove(g, actor, m as any, ctx.now);
      return { g: r.g, note: r.g.result ? resultText(r.g) : `${me} played ${r.san}` };
    }
    if (type === 'resign') {
      return { g: { ...g, result: { winner: (actor ^ 1) as 0 | 1, reason: 'resign' }, drawOffer: null }, note: `${me} resigned` };
    }
    if (type === 'offer_draw') {
      if (g.drawOffer === (actor ^ 1)) return { g: { ...g, result: { winner: null, reason: 'agreement' }, drawOffer: null }, note: 'Draw agreed' };
      if (g.drawOffer === actor) throw new RuleError('Your draw offer is already on the table');
      if (g.offers[actor] >= MAX_DRAW_OFFERS) throw new RuleError('No more draw offers this game');
      const offers: [number, number] = [g.offers[0], g.offers[1]];
      offers[actor]++;
      return { g: { ...g, drawOffer: actor as 0 | 1, offers }, note: `${me} offers a draw` };
    }
    if (type === 'accept_draw') {
      if (g.drawOffer !== (actor ^ 1)) throw new RuleError('There is no draw offer to accept');
      return { g: { ...g, result: { winner: null, reason: 'agreement' }, drawOffer: null }, note: 'Draw agreed' };
    }
    if (type === 'decline_draw') {
      if (g.drawOffer !== (actor ^ 1)) throw new RuleError('There is no draw offer to decline');
      return { g: { ...g, drawOffer: null }, note: `${me} declined the draw` };
    }
    throw new RuleError('Unknown move');
  },
  bot: {
    move: (g, _priv, seat, ctx) => botChessMove(g, seat, ctx.rng),
    names: ['Bishop Bot', 'Knight Bot'],
  },
  // A human who stops moving forfeits (the bot never plays for a person).
  onIdle: (g, _privs, seat, ctx) => (g.result ? null : {
    g: { ...g, result: { winner: (seat ^ 1) as 0 | 1, reason: 'abandoned' }, drawOffer: null },
    note: `${nameAt(ctx, seat)} stopped moving — forfeit`,
  }),
  wakeAt: (g) => (g.clock && !g.result ? g.clock.since + g.clock.ms[turnOf(g)] : null),
  onWake: (g, _privs, ctx) => {
    if (!g.clock || g.result) return { g };
    if ((clockLeft(g, turnOf(g), ctx.now) ?? 1) > 0) return { g }; // clock moved on (a move landed first)
    const f = flag(g, ctx.now);
    return { g: f, note: resultText(f) };
  },
  isOver: (g) => !!g.result,
  results: (g): SeatResult[] => [0, 1].map((seat) => ({
    seat,
    outcome: g.result?.winner === null || g.result?.winner === undefined ? 'draw' : g.result.winner === seat ? 'win' : 'loss',
    points: g.result?.winner === seat ? 1 : 0,
  })),
};
